import type { PayloadAction } from '@reduxjs/toolkit';
import type { IProcosState } from '../../../../types';
import { addMarketFeatureMA, revertFeaturesMA } from '../../../services/DataHelperFunctions';
import { isArraysEqual } from '../../../services/Util';
import { removeMarketFeatureMA } from '../../Helper/SliceDataHelper';

import {
  compareActiveStatus,
  handleCheckBoxSelected,
  handleCheckBoxUnselect,
  isAllFeatureMarketEqual,
  resetFeatureSaveStatus,
} from './ProcosReducerHelpers';

export const marketAssociationReducers = {
  updateProductMarketAssociations(state: IProcosState, action: PayloadAction<any>): void {
    state.isDataUpdated = true;
    state.isCreateRuleInvoked = false;
    resetFeatureSaveStatus(state);
    if (action.payload.checked) {
      //Checkbox is checked, add it to associations
      if (state.product.ma) {
        state.product.ma.updatedMarkets.push(action.payload.market);
      } else {
        state.product.ma = {
          initialMarkets: [],
          updatedMarkets: [action.payload.market],
        };
      }
      //Update Features Market associations to have the market added at Product level
      state.features.ma = addMarketFeatureMA(state.features.ma, action.payload.market);
    } else {
      //Unchecked checkbox hence remove it from associations
      const index = state.product.ma?.updatedMarkets.indexOf(action.payload.market);
      state.product.ma?.updatedMarkets.splice(index, 1);
      state.features.ma = removeMarketFeatureMA(
        state.features.ma,
        action.payload.market,
        state.product.ma?.updatedMarkets.length
      );
    }
    // if there are no changes to market at product level then disable the buttons
    if (isArraysEqual(state.product.ma.initialMarkets, state.product.ma.updatedMarkets)) {
      state.isDataUpdated = false;
    }
  },
  updateFeatureMarketAssociations(state: IProcosState, action: PayloadAction<any>): void {
    const featureKey = state.features.ma?.[action.payload.id];
    if (!state.isDataUpdated) {
      state.isDataUpdated = true;
      state.isCreateRuleInvoked = false;
      resetFeatureSaveStatus(state);
    }
    if (featureKey) {
      //When feature is already present in associations, edit it
      if (action.payload.checked) {
        //checkbox is checked
        handleCheckBoxSelected(state, action, featureKey);
      } else {
        //checkbox is unchecked
        handleCheckBoxUnselect(state, action, featureKey);
      }
    } else {
      //When feature is not present in associations, Add it
      //since no key is present, its always uncheck case
      const newMarkets = state.product.ma?.updatedMarkets.filter(
        (market) => market !== action.payload.market
      );
      state.features.ma = {
        ...state.features.ma,
        [action.payload.id]: {
          initialMarkets: [],
          updatedMarkets: newMarkets,
        },
      };
      if (newMarkets?.length === 0) {
        if (state.features.status.updated) {
          state.features.status.updated[action.payload.id] = false;
        }
      }
      //Add Feature Type
      state.features.ma[action.payload.id].type = action.payload.featureType;
    }
    const initialActiveStatus = state.features.status.initial;
    const updatedActiveStatus = state.features.status.updated;
    // if there are no changes to feature market associations and Active status
    // then disable the buttons
    if (
      isAllFeatureMarketEqual(state.features.ma) &&
      compareActiveStatus(initialActiveStatus || {}, updatedActiveStatus || {})
    ) {
      state.isDataUpdated = false;
    }
  },
  resetFeatureMA(state: IProcosState, action: PayloadAction<any>): void {
    resetFeatureSaveStatus(state);
    if (!state.isDataUpdated) {
      state.isDataUpdated = true;
    }
    if (action.payload.id) {
      //RESET
      const featureKey = state.features.ma[action.payload.id];
      /* Initial countries 0 indicates they are selcted by user in current session and association 
                    can be deleted while reset since there is no need to create a rule as it already follows product level*/
      if (featureKey.initialMarkets.length === 0) {
        delete state.features.ma[action.payload.id];
        return;
      }
      //Update the countries of features to Product Countries
      state.features.ma[action.payload.id].type = action.payload.featureType;
      state.features.ma[action.payload.id].updatedMarkets = [...state.product.ma.updatedMarkets];
    } else {
      state.features.status.updated = state.features.status ? state.features.status?.initial : null;
      //RESET ALL
      //loop through all associations and reset all.
      Object.keys(state.features.ma).forEach(function (key) {
        /* Initial countries 0 indicates they are selcted by user in current session and association 
                    can be deleted while reset since there is no need to create a rule as it already follows product level*/
        if (state.features.ma[key].initialMarkets.length === 0) {
          delete state.features.ma[key];
          return;
        }
        //Update the countries of features to Product Countries
        state.features.ma[key].updatedMarkets = [...state.product.ma.updatedMarkets];
      });
    }
  },
  revertMarketAssociations(state: IProcosState): void {
    state.product.ma.updatedMarkets =
      state.product.ma === null ? [] : state.product.ma?.initialMarkets;
    state.features.status.updated = state.features.status ? state.features.status?.initial : null;
    state.features.ma = revertFeaturesMA(
      { ...state.features.ma },
      state.product.ma?.updatedMarkets
    );
    state.isDataUpdated = false;
    state.featureStatus = false;
    state.isProductStatusUpdated = false;
  },
  updateToInitialFeatureMA(state: IProcosState, action: PayloadAction<any>): void {
    if (action.payload) {
      action.payload.forEach((key: string) => {
        const featureKey = state.features.ma?.[key];
        if (featureKey) {
          state.features.ma = {
            ...state.features.ma,
            [key]: {
              initialMarkets: state.features.ma?.[key].initialMarkets,
              updatedMarkets: state.features.ma?.[key].updatedMarkets,
            },
          };
        } else {
          state.features.ma = {
            ...state.features.ma,
            [key]: {
              initialMarkets: [],
              updatedMarkets: state.product.ma?.updatedMarkets,
            },
          };
        }
      });
    }
  },
};
